import type { MemCommonEditorStore } from "@/components/note/store/mem-common-editor-store";
import { ZIndex } from "@/domains/design/constants";
import {
  MDSButton,
  MDSButtonSize,
  MDSButtonVariant,
  MDSText,
  MDSTextColor,
  MDSTextVariant,
} from "@/mds";
import styled from "@emotion/styled";
import {
  editorOutlineModule,
  type MemCommonEditorOutlineItem,
  type MemCommonEditorOutlineState,
} from "@mem-labs/common-editor";
import { observer } from "mobx-react-lite";
import { type MutableRefObject, useCallback, useEffect, useRef, useState } from "react";
import { getNoteOutlineVisibility } from "./note-outline-visibility";

const OUTLINE_WIDTH = 232;
const OUTLINE_EDGE_OFFSET = 20;
const OUTLINE_SCROLL_ANIMATION_DURATION_MS = 150;
const OUTLINE_VISIBILITY_ANIMATION_DURATION_MS = 160;

const EMPTY_OUTLINE_STATE: MemCommonEditorOutlineState = {
  headingItems: [],
  collapsibleToggleCount: 0,
  collapsedToggleCount: 0,
};

interface NoteOutlineProps {
  editorStore?: MemCommonEditorStore;
  isOutlineOpen: boolean;
  topOffset: string;
}

export const NoteOutline = observer<NoteOutlineProps>(function NoteOutline({
  editorStore,
  isOutlineOpen,
  topOffset,
}) {
  const editor = editorStore?.editor;
  const [outlineState, setOutlineState] =
    useState<MemCommonEditorOutlineState>(EMPTY_OUTLINE_STATE);
  const [isOutlineAnimationVisible, setIsOutlineAnimationVisible] = useState(false);
  const [shouldRenderOutlinePanel, setShouldRenderOutlinePanel] = useState(false);
  const scrollAnimationFrameRef = useRef<number | null>(null);

  const refreshOutlineState = useCallback(() => {
    setOutlineState(editorOutlineModule.getOutlineState({ editor }));
  }, [editor]);

  useEffect(() => {
    if (!editor) {
      setOutlineState(EMPTY_OUTLINE_STATE);
      return;
    }

    refreshOutlineState();
    editor.on("transaction", refreshOutlineState);

    return () => {
      editor.off("transaction", refreshOutlineState);
    };
  }, [editor, refreshOutlineState]);

  const getEditorViewportElement = useCallback(() => {
    const editorElement = editor?.view.dom;
    return editorElement?.closest<HTMLElement>("[data-note-editor-viewport='true']") ?? null;
  }, [editor]);

  const handleNavigate = useCallback(
    (item: MemCommonEditorOutlineItem) => {
      if (!editor) return;

      const viewportElement = getEditorViewportElement();
      if (!viewportElement) return;

      cancelScrollAnimation({ animationFrameRef: scrollAnimationFrameRef });
      navigateToOutlineItemWithAnimatedScroll({
        animationFrameRef: scrollAnimationFrameRef,
        editor,
        item,
        scrollContainer: viewportElement,
      });
      refreshOutlineState();
    },
    [editor, getEditorViewportElement, refreshOutlineState]
  );

  const hasHeadingItems = outlineState.headingItems.length > 0;
  const isEditorReady = Boolean(editor && editorStore?.editorContentRendered);

  const { shouldShowOutline } = getNoteOutlineVisibility({
    isEditorReady,
    isOutlineOpen,
  });

  useEffect(() => {
    if (shouldShowOutline) {
      setShouldRenderOutlinePanel(true);
      return;
    }

    setIsOutlineAnimationVisible(false);

    if (!shouldRenderOutlinePanel) return;

    const timeoutId = window.setTimeout(() => {
      setShouldRenderOutlinePanel(false);
    }, OUTLINE_VISIBILITY_ANIMATION_DURATION_MS);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [shouldRenderOutlinePanel, shouldShowOutline]);

  useEffect(() => {
    if (!shouldRenderOutlinePanel) return;

    const animationFrameId = window.requestAnimationFrame(() => {
      setIsOutlineAnimationVisible(shouldShowOutline);
    });

    return () => {
      window.cancelAnimationFrame(animationFrameId);
    };
  }, [shouldRenderOutlinePanel, shouldShowOutline]);

  useEffect(() => {
    return () => {
      cancelScrollAnimation({ animationFrameRef: scrollAnimationFrameRef });
    };
  }, []);

  return (
    <>
      {shouldRenderOutlinePanel && (
        <OutlineContainer
          $isVisible={shouldShowOutline && isOutlineAnimationVisible}
          $topOffset={topOffset}
          data-test-id="note-outline-panel"
        >
          <OutlineHeader>
            <OutlineTitle>Outline</OutlineTitle>
          </OutlineHeader>
          {hasHeadingItems ? (
            <OutlineList>
              {outlineState.headingItems.map(item => (
                <OutlineItemButton
                  key={item.id}
                  $isActive={item.isActive}
                  $level={item.level}
                  aria-current={item.isActive ? "location" : undefined}
                  data-test-id="note-outline-item"
                  onClick={() => handleNavigate(item)}
                  size={MDSButtonSize.Small}
                  type="button"
                  variant={MDSButtonVariant.Transparent}
                >
                  <OutlineItemText>{item.text}</OutlineItemText>
                </OutlineItemButton>
              ))}
            </OutlineList>
          ) : (
            <OutlineEmptyState data-test-id="note-outline-empty-state">
              <MDSText variant={MDSTextVariant.ParagraphS} color={MDSTextColor.Tertiary}>
                Headings you add to the note will appear here
              </MDSText>
            </OutlineEmptyState>
          )}
        </OutlineContainer>
      )}
    </>
  );
});

type NavigateToOutlineItemWithAnimatedScrollInput = {
  animationFrameRef: MutableRefObject<number | null>;
  editor: NonNullable<MemCommonEditorStore["editor"]>;
  item: MemCommonEditorOutlineItem;
  scrollContainer: HTMLElement;
};

const navigateToOutlineItemWithAnimatedScroll = ({
  animationFrameRef,
  editor,
  item,
  scrollContainer,
}: NavigateToOutlineItemWithAnimatedScrollInput): void => {
  const textSelectionPos = Math.min(item.pos + 1, editor.state.doc.content.size);
  const outlineItemElement = getOutlineItemElement({
    editor,
    pos: item.pos,
  });

  editor.commands.focus(textSelectionPos, {
    scrollIntoView: false,
  });

  if (!outlineItemElement) {
    editor.commands.scrollIntoView();
    return;
  }

  animateScrollToElement({
    animationFrameRef,
    durationMs: OUTLINE_SCROLL_ANIMATION_DURATION_MS,
    scrollContainer,
    targetElement: outlineItemElement,
  });
};

type OutlineItemElementInput = {
  editor: NonNullable<MemCommonEditorStore["editor"]>;
  pos: number;
};

const getOutlineItemElement = ({ editor, pos }: OutlineItemElementInput): HTMLElement | null => {
  const node = editor.view.nodeDOM(pos);
  const ownerWindow = editor.view.dom.ownerDocument.defaultView;

  if (!ownerWindow || !(node instanceof ownerWindow.HTMLElement)) {
    return null;
  }

  return node;
};

type ScrollAnimationInput = {
  animationFrameRef: MutableRefObject<number | null>;
  durationMs: number;
  scrollContainer: HTMLElement;
  targetElement: HTMLElement;
};

const animateScrollToElement = ({
  animationFrameRef,
  durationMs,
  scrollContainer,
  targetElement,
}: ScrollAnimationInput): void => {
  const ownerWindow = scrollContainer.ownerDocument.defaultView;

  if (!ownerWindow || ownerWindow.matchMedia("(prefers-reduced-motion: reduce)").matches) {
    targetElement.scrollIntoView({
      block: "start",
      inline: "nearest",
    });
    return;
  }

  const containerRect = scrollContainer.getBoundingClientRect();
  const targetRect = targetElement.getBoundingClientRect();
  const startScrollTop = scrollContainer.scrollTop;
  const maxScrollTop = Math.max(0, scrollContainer.scrollHeight - scrollContainer.clientHeight);
  const targetScrollTop = clamp({
    max: maxScrollTop,
    min: 0,
    value: startScrollTop + targetRect.top - containerRect.top,
  });
  const scrollDistance = targetScrollTop - startScrollTop;

  if (scrollDistance === 0) {
    return;
  }

  const startTime = ownerWindow.performance.now();

  const step = (time: number) => {
    const elapsedTime = time - startTime;
    const progress = clamp({
      max: 1,
      min: 0,
      value: elapsedTime / durationMs,
    });
    const easedProgress = easeOutCubic(progress);

    scrollContainer.scrollTop = startScrollTop + scrollDistance * easedProgress;

    if (progress < 1) {
      animationFrameRef.current = ownerWindow.requestAnimationFrame(step);
      return;
    }

    animationFrameRef.current = null;
  };

  animationFrameRef.current = ownerWindow.requestAnimationFrame(step);
};

type CancelScrollAnimationInput = {
  animationFrameRef: MutableRefObject<number | null>;
};

const cancelScrollAnimation = ({ animationFrameRef }: CancelScrollAnimationInput): void => {
  if (animationFrameRef.current === null) return;

  window.cancelAnimationFrame(animationFrameRef.current);
  animationFrameRef.current = null;
};

type ClampInput = {
  max: number;
  min: number;
  value: number;
};

const clamp = ({ max, min, value }: ClampInput): number => Math.min(max, Math.max(min, value));

const easeOutCubic = (progress: number): number => 1 - Math.pow(1 - progress, 3);

const OutlineContainer = styled.aside<{
  $isVisible: boolean;
  $topOffset: string;
}>`
  position: absolute;
  top: ${({ $topOffset }) => `calc(${$topOffset} + ${OUTLINE_EDGE_OFFSET}px)`};
  right: ${OUTLINE_EDGE_OFFSET}px;
  z-index: ${ZIndex.FloatingBar};
  width: ${OUTLINE_WIDTH}px;
  max-height: ${({ $topOffset }) => `calc(100% - ${$topOffset} - ${OUTLINE_EDGE_OFFSET * 2}px)`};
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
  padding-block: ${({ theme }) => theme.spacing[3]};
  padding-inline: 0;
  background: ${({ theme }) => theme.colors.background.button.subtle.default};
  border: 0;
  border-radius: ${({ theme }) => theme.borderRadius.large};
  box-shadow: ${({ theme }) => theme.shadows.surface};
  cursor: default;
  opacity: ${({ $isVisible }) => ($isVisible ? 1 : 0)};
  pointer-events: ${({ $isVisible }) => ($isVisible ? "auto" : "none")};
  transform: ${({ $isVisible }) => ($isVisible ? "translateX(0)" : "translateX(8px)")};
  transition:
    opacity ${OUTLINE_VISIBILITY_ANIMATION_DURATION_MS}ms ease,
    transform ${OUTLINE_VISIBILITY_ANIMATION_DURATION_MS}ms cubic-bezier(0.2, 0, 0, 1);
  will-change: opacity, transform;

  @media (prefers-reduced-motion: reduce) {
    transform: none;
    transition: none;
  }
`;

const OutlineHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing[2]};
  padding: ${({ theme }) => `${theme.spacing[2]} ${theme.spacing[5]}`};
`;

const OutlineTitle = styled.div`
  min-width: 0;
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: 12px;
  font-weight: 600;
  line-height: 16px;
`;

const OutlineList = styled.nav`
  display: flex;
  flex: 1;
  min-height: 0;
  flex-direction: column;
  gap: 2px;
  overflow-y: auto;
  padding: 0;

  &::-webkit-scrollbar {
    display: none;
  }

  -ms-overflow-style: none;
  scrollbar-width: none;
`;

const OutlineEmptyState = styled.div`
  display: flex;
  flex: 1;
  min-height: 84px;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  gap: 10px;
  padding-inline: ${({ theme }) => theme.spacing[5]};
`;

const OutlineItemButton = styled(MDSButton, {
  shouldForwardProp: prop => !prop.startsWith("$"),
})<{
  $isActive: boolean;
  $level: number;
}>`
  border-radius: 0;
  background: ${({ theme, $isActive }) =>
    $isActive ? theme.colors.background.button.subtle.hover : "transparent"};
  color: ${({ theme, $isActive }) =>
    $isActive ? theme.colors.text.primary : theme.colors.text.secondary};
  justify-content: flex-start;
  min-height: 28px;
  width: 100%;
  padding: ${({ theme, $level }) => {
    const indent = (Math.min($level, 4) - 1) * 12;
    return `5px ${theme.spacing[5]} 5px calc(${theme.spacing[5]} + ${indent}px)`;
  }};
  text-align: left;

  &:hover {
    background: ${({ theme }) => theme.colors.background.button.subtle.hover};
    color: ${({ theme }) => theme.colors.text.primary};
  }

  &[data-hovered]:not([data-disabled]) {
    background: ${({ theme }) => theme.colors.background.button.subtle.hover};
    color: ${({ theme }) => theme.colors.text.primary};
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.border.emphasis};
    outline-offset: 1px;
  }
`;

const OutlineItemText = styled.span`
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 12px;
  line-height: 16px;
`;
